import React, { useEffect, useState } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { confirmEmail } from "../../utils/Auth";
import Skeleton from "@mui/material/Skeleton";
import { useTranslation } from "react-i18next";
import AccountConfirmation from "../../image/AccountConfirmation.svg";
import ServerError from "../../image/ServerError.svg";
import { useTheme } from "@mui/material/styles";
import { Box, Grid, Button, Typography } from "@mui/material";

/**
 * Email confirmation stranica.
 * Podržava:
 *  - /email/confirmation?userId=...&token=...   (ili ?userid=...)
 *  - /email/confirmation/:userid/:token         (ili /:id/:token)
 */

function safeDecodeTwice(value) {
  let out = value ?? "";
  try { out = decodeURIComponent(out); } catch {}
  try { out = decodeURIComponent(out); } catch {}
  return out;
}

function EmailConfirmation() {
  // QUERY: /email/confirmation?userId=...&token=...
  const [searchParams] = useSearchParams();
  const queryUserId =
    searchParams.get("userId") || // camelCase (kao u tvom linku)
    searchParams.get("userid");   // fallback

  const queryToken = searchParams.get("token");

  // PATH: /email/confirmation/:userid/:token  ili  /email/confirmation/:id/:token
  const { userid: pathUserId, id: pathId, token: pathToken } = useParams();

  // Odabir vrednosti (prioritet query → :userid → :id)
  const userId = (queryUserId || pathUserId || pathId || "").trim();
  const rawToken = (queryToken || pathToken || "").trim();

  const [validationSuccess, setValidationSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    // Debug: vidi u konzoli da li smo pokupili vrednosti iz URL-a
    console.log("[EmailConfirmation] userId:", userId);
    console.log("[EmailConfirmation] rawToken len:", rawToken?.length);

    if (!userId || !rawToken) {
      setLoading(false);
      setValidationSuccess(false);
      return;
    }

    // Token zna da bude duplo enkodovan (%2B, %2F, ...).
    const securityToken = safeDecodeTwice(rawToken);
    console.log("[EmailConfirmation] decoded token len:", securityToken.length);

    confirmEmail(userId, securityToken)
      .then((res) => {
        // Radi i ako Response ima statusCode ili status
        const code = res?.statusCode ?? res?.status ?? 0;
        console.log("[EmailConfirmation] API result status:", code, res);
        const ok = code >= 200 && code < 300;
        setValidationSuccess(ok);
        setLoading(false);
      })
      .catch((err) => {
        console.error("[EmailConfirmation] API error:", err);
        setLoading(false);
      });
  }, [userId, rawToken]);

  return loading ? (
    <Grid
      container
      spacing={4}
      direction="column"
      alignItems="center"
      justifyContent="center"
      py={2}
      sx={{ minHeight: "78vh", width: "100vw" }}
    >
      <Grid item>
        <Skeleton animation="wave" variant="rectangular" sx={{ height: "2vh", width: "50vw" }} />
      </Grid>
      <Grid item>
        <Skeleton animation="wave" variant="rectangular" sx={{ height: "2vh", width: "50vw" }} />
      </Grid>
      <Grid item>
        <Skeleton animation="wave" variant="rectangular" sx={{ height: "2vh", width: "50vw" }} />
      </Grid>
    </Grid>
  ) : (
    <Grid
      container
      spacing={0}
      direction="row"
      alignItems="center"
      justifyContent="center"
      py={2}
      sx={{ minHeight: "50vh" }}
    >
      <Grid item xs={12} md={6} mt={5}>
        <Box sx={{ p: { md: "10vh" } }}>
          <img src={validationSuccess ? AccountConfirmation : ServerError} alt="message" />
        </Box>
      </Grid>

      <Grid item xs={10} md={4}>
        <Grid container direction="column" justifyContent="center" alignItems="center">
          <Grid item xs={10} md={4}>
            <Typography variant="h4">
              {validationSuccess ? t("emailConfirmation.success") : t("emailConfirmation.error")}
            </Typography>
            {!userId || !rawToken ? (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t("emailConfirmation.missingParams") || "Invalid confirmation link (missing userId or token)."}
              </Typography>
            ) : null}
          </Grid>

          <Grid item xs={12}>
            <Button
              onClick={() => navigate("/")}
              size="large"
              variant="contained"
              sx={{
                backgroundColor: theme.palette.secondary.main,
                my: { xs: "5vh", md: "10vh" },
              }}
            >
              {t("emailconfirmation.click")} {t("emailconfirmation.here")}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default EmailConfirmation;
